// @flow
import React from 'react';
import { createUseStyles } from 'react-jss';
import Button from '@ff/ui/Button';
import Link from '@ff/ui/Link';
import Image from './Background/Image';
import Video from './Background/Video';
import type { Styles } from 'shared/types';
import type { Props } from './props-schema';
import CleanedHtml from 'shared/components/CleanedHtml';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';
import { isiOS, isMobile } from '@ff/ui/utils/isiOS';

export default function Slide(props: Props) {
  const {
    color_theme,
    slide_link,
    bg_image,
    bg_video,
    highlights,
    buttons,
    appDownload,
  } = props;
  const c: Styles<typeof styles> = useStyles(props);

  const slideContent = (
    <>
      {/* Background image */}
      <Image
        defaultImg={bg_image.url}
        mobileImg={bg_image.mobile_url}
        responsiveImgs={bg_image.responsive_urls}
        enableGradient={bg_image.enable_gradient}
        imageAlt={bg_image?.image_alt || ''}
      />
      {/* Background video */}
      {
        bg_video?.url &&
        <Video backgroundVideo={bg_video} />
      }
    </>
  );

  return (
    <div className={c.slide}>
      {/* Make content clickable */}
      {
        slide_link && (
          <a target="_blank" rel="noopener noreferrer" href={slide_link}>
            {slideContent}
          </a>
        )
      }
      {
        !slide_link && slideContent
      }
      {/* Highlights - overlay */}
      <div className={c.container} >
        <div className={c.highlights}>
          {
            highlights && highlights.map((h) => (
              <div key={`${h?.title}-${h?.description}`} className={c.highlightsTextContainer}>
                {/* Keep default maxWidth: 100% */}
                {h?.title && (
                  <a style={{ maxWidth: isMobile() ? '95%' : `${h?.title_width ? h.title_width : 100}%` }} className={c.highlightTitle} href={slide_link}>
                    <CleanedHtml as='div'>
                      {h.title}
                    </CleanedHtml>
                  </a>
                )}
                {h?.description && (
                  <a style={{ maxWidth: `${h?.description_width ? h.description_width : 100}%` }} className={c.highlightDescription} href={slide_link}>
                    {h.description}
                  </a>
                )}
                {!!h?.hide_underline || <hr className={c.hr} />}
                {h?.subtitle && (
                  <a style={{ maxWidth: '100%' }} className={c.highlightSubtitle} href={slide_link}>
                    {h.subtitle}
                  </a>
                )}
              </div>
            ))
          }
        </div>

        {/* Buttons */}
        <div className={c.buttons}>
          {buttons?.map((b, i) => (
            <SlideButton
              key={i}
              button={b}
              colorTheme={color_theme}
              appDownload={appDownload}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const SlideButton = (props) => {
  const {
    button,
    appDownload,
    colorTheme
  } = props;

  const c: Styles<typeof styles> = useStyles(props);

  // Open the app, app store, or mobile-app page, depending on device and app install status.
  const onClick = (e) => {
    if (!button.link_to_app) return;
    if (!window) return;
    if (!appDownload) {
      console.error('Props are not yet configured for this button');
      return;
    }

    e.preventDefault();

    // Initial setup
    const isCN = window.location.pathname.startsWith('/cn') || window.location.hostname.includes('faradayfuturecn');
    const iOS = isiOS();
    const delay = iOS ? 5000 : 500;

    // Desktop - go to mobile-app page
    if (!isMobile() && appDownload.url) {
      window.location.href = appDownload.url;
      return;
    }

    const appLink = iOS ? appDownload.iosAppLink :
      isCN ? appDownload.androidAppLink : appDownload.androidApkLink;
    const storeLink = iOS ? appDownload.iosStoreLink : appDownload.androidStoreLink;

    // will console error and not redirect if app not installed
    if (appLink) window.location.href = appLink;

    const loadTimestamp = Date.now();

    if (storeLink) {
      setTimeout(() => {
        // go to app store if deep link fails
        const timeoutTimestamp = Date.now();

        if (!iOS || timeoutTimestamp - loadTimestamp < 1000) {
          window.location.href = storeLink;
        }
      }, delay);
    }
  };


  if (button.button_type === 'DEFAULT') {
    return (
      <Link
        href={button.link_to_app ? undefined : button.url}
        onClick={button.link_to_app ? onClick : undefined}
      >
        <Button
          variant={colorTheme === 'DARK' ? 'dark' : 'outlinePrimary'}
          border='none'
          pl='100px'
          pr='100px'
          tabIndex='-1'
        >
          {button.label}
        </Button>
      </Link>
    );
  }

  return (
    <div key={button.label} className={c.itemTextContainer}>
      <Link
        target={button.open_in_new_tab ? '_blank' : null}
        rel='noreferrer noopener'
        href={button.link_to_app ? undefined : button.url}
        onClick={button.link_to_app ? onClick : undefined}
      >
        <p className={c.linkText}>{button.label}</p>
      </Link>
    </div>
  );
};

const styles = {
  slide: {
    position: 'relative',
    '&.slick-slide': {
      willChange: 'transform'
    }
  },
  container: {
    position: 'absolute',
    top: '18vh',
    width: '100%',
    '@media(min-width: 900px)': {
      top: '18vh',
    },
  },
  highlights: {
    opacity: '0',
    color: ({ color_theme: color }) => color === 'DARK' ? 'black' : 'white',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    fontFamily: FONT_FAMILY_ROBOTO,
    fontWeight: '300',
    '&> div': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      '& div': {
        textAlign: 'center',
      },
    },
    '@media(min-width: 577px)': {
      justifyContent: 'center',
      fontSize: '16.5px',
      '&> div': {
        justifyContent: 'normal',
        margin: '0px 24px',
      }
    },
    animation: '$slideDownFadeIn 1s ease-in-out 0.5s',
    animationFillMode: 'forwards',
  },
  highlightTitle: {
    color: ({ color_theme: color }) => color === 'DARK' ? '#171A20' : 'rgba(224, 223, 231, 1)',
    fontSize: '30px',
    fontWeight: 400,
    letterSpacing: '0.18px',
    lineHeight: '35.55px',
    margin: '0px 16px 3px 16px',
    textDecoration: 'none',
    '@media(min-width: 900px)': {
      fontSize: '38px',
      letterSpacing: '0.288px',
      lineHeight: '45.03px',
    },
    '@media(min-width: 1200px)': {
      fontSize: '40px',
      letterSpacing: '0.24px',
      lineHeight: '48px',
    },
  },
  highlightDescription: {
    color: ({ color_theme: color }) => color === 'DARK' ? '#171A20' : 'rgba(224, 223, 231, 1)',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    margin: '0px 16px 6px 16px',
    textAlign: 'center',
    width: 'fit-content',
    textDecoration: 'none',
    '@media(min-width: 900px)': {
      fontSize: '20px',
      lineHeight: '28px',
      marginBottom: '8px'
    },
    '@media(min-width: 1800px)': {
      fontSize: '28px',
      marginBottom: '15px'
    },
  },
  highlightSubtitle: {
    color: ({ color_theme: color }) => color === 'DARK' ? '#6A6C70' : '#C9C9D1',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '20px',
    margin: '0px 16px 6px 16px',
    textAlign: 'center',
    textDecoration: 'none',
    '@media(min-width: 900px)': {
      paddingBottom: '5px',
      paddingTop: '5px',
      marginBottom: '8px'
    },
    '@media(min-width: 1800px)': {
      fontSize: '20px',
      lineHeight: '20px',
      marginBottom: '15px'
    },
  },
  buttons: {
    opacity: 0,
    animation: '$slideDownFadeIn 1s ease-in-out 0.5s',
    animationFillMode: 'forwards',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '35px',
    marginTop: ({ button_margin_top }) => button_margin_top || '0px',
  },
  hr: {
    width: '70px',
    maxWidth: '153px',
    borderWidth: '1px 0px 0px 0px',
    borderColor: ({ color_theme: color }) => color === 'DARK' ? 'black' : 'white',
    backgroundColor: ({ color_theme: color }) => color === 'DARK' ? 'black' : 'white',
    '@media(min-width: 577px)': {
      width: '16vw',
    }
  },
  image: {
    width: '100%',
  },
  itemTextContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  itemsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '35px',
    '@media(min-width: 900px)': {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '5px',
    },
  },
  linkText: {
    // SlideButton: Use "colorTheme"
    color: ({ colorTheme: color }) => color === 'DARK' ? 'rgba(105, 101, 224, 1)' : 'rgba(225, 225, 225, 1)',
    fontSize: '16px',
    lineHeight: '24px',
    textDecoration: 'underline',
    marginBottom: '5px',
    marginTop: '5px',
    '@media(min-width: 900px)': {
      fontSize: '17px',
      lineHeight: '22px',
    },
    '@media(min-width: 1200px)': {
      fontSize: '18px',
      lineHeight: '26px',
    },
    '@media(min-width: 1800px)': {
      fontSize: '21px',
      lineHeight: '30px',
    },
  },
  highlightsTextContainer: {
    width: '100%',
  },
  '@keyframes slideDownFadeIn': {
    from: {
      transform: 'translateY(-20px)',
      opacity: '0'
    },
    to: {
      transform: 'translateY(0px)',
      opacity: '1'
    }
  }
};

const useStyles = createUseStyles(styles);
